import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, merge, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../../services/shared/snackbar/snackbar.service';
import { TranslationService } from '../../../services/translation/translation.service';
import { UtilsService } from '../../../services/shared/utils/utils.service';
import { CommonService } from '../../../services/shared/common/common.service';
import { LoadingService } from '../../../services/loading/loading.service';
import { PagedResult } from '../../../models/pagedResult.model';
import { PageStats } from '../../../models/pageStats.model';
import { StatService } from '../../../services/stat/stat.service';
import { HttpResponse } from '@angular/common/http';
import { CompletionStatus } from '../../../shared/enums/completion-status/completion-status.module';
import { FilterDescriptor } from '../../../models/filterDescriptor.model';
import { DatePipe } from '@angular/common';
import { ComponentType } from '../../../shared/enums/component-type/component-type.module';
import { FilterApplied } from '../../../models/filterApplied.model';

@Component({
  selector: 'app-stats',
  templateUrl: './stats.component.html',
  styleUrl: './stats.component.css'
})
export class StatsComponent implements OnInit {
  private destroy$ = new Subject<void>();

  displayedColumns: string[] = [
    'epj',
    'pageId',
    'title',
    'pageCreationDate',
    'pageTaux',
    'pageStatus',
    'offerId',
    'offerStatus',
    'soprodId',
    'expirationDate',
    'offerCreationDate',
    'firstAccessByClient',
    'lastAccessByClient',
    'lastModificationByClient',
    'lastModificationAny'
  ];
  columnLabels: Record<string, string> = {
    epj: 'EPJ',
    pageId: 'ID page',
    title: 'Titre',
    pageCreationDate: 'Création page',
    pageTaux: '%',
    pageStatus: 'Statut page',
    offerId: 'ID offre',
    offerStatus: 'Statut offre',
    soprodId: 'ID Soprod',
    expirationDate: 'Expiration',
    offerCreationDate: 'Création offre',
    firstAccessByClient: 'Premier accès',
    lastAccessByClient: 'Dernier accès',
    lastModificationByClient: 'Dernière modif. (client)',
    lastModificationAny: 'Dernière modif.'
  };

  filterColumns = this.displayedColumns.map(c => `${c}_filter`);
  dataSource = new MatTableDataSource<PageStats>();
  totalItems = 0;

  availableFilters: FilterDescriptor[] = [];
  activeFilters: { [key: string]: FilterApplied | undefined } = {};

  ComponentType = ComponentType;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChildren('filterInput') filterInputs!: QueryList<ElementRef>;

  constructor(private statsService: StatService,
    public dialog: MatDialog,
    private snackBar: SnackbarService,
    public translationService: TranslationService,
    public utils: UtilsService,
    public common: CommonService,
    private loadingService: LoadingService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe) { }

    columnFormatters: Record<string, (v: any) => string> = {
      pageStatus: (v: any) => CompletionStatus[v],
      offerStatus: (v: any) => CompletionStatus[v],
      pageCreationDate: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      expirationDate: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      offerCreationDate: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      firstAccessByClient: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      lastAccessByClient: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      lastModificationByClient: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? '',
      lastModificationAny: (v: string) => this.datePipe.transform(v, 'dd/MM/yyyy HH:mm') ?? ''
    };

  ngOnInit() {
    this.getPagesStatsFilters();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    merge(this.paginator.page, this.sort.sortChange)
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.getPagesStats();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter(resetFilters: boolean = false): void{
    if(resetFilters){
      this.activeFilters = {};
      this.filterInputs.forEach(input => {
        input.nativeElement.value = '';
      });
    }
    this.paginator.pageIndex = 0;
    this.getPagesStats();
  }

  getPagesStatsFilters() {
    this.loadingService.show();

    this.statsService.getPagesStatsFilters()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: FilterDescriptor[]) => {
        this.availableFilters = result;
        this.getPagesStats();
      },
      error: (error: any) => {
        this.loadingService.hide();
        this.snackBar.showError(this.translationService.translate('Error fetching stats'));
      }
    });
  }

  getPagesStats() {
    const pageIndex = this.paginator?.pageIndex ?? 0;
    const pageSize = this.paginator?.pageSize ?? 10;
    this.loadingService.show();
    this.statsService.getPagesStats(pageIndex + 1, pageSize, this.activeFilters)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: PagedResult<PageStats>) => {
        this.totalItems = result.totalCount;

        this.ngZone.run(() => {
          this.dataSource.data = result.items;
        });

        this.loadingService.hide();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loadingService.hide();
        this.snackBar.showError(this.translationService.translate('Error fetching stats'));
      }
    });
  }

  onExportStats(): void {
    this.loadingService.show();
    this.statsService.exportPagesStats(this.activeFilters)
    .subscribe({
      next: (result: HttpResponse<Blob>) => {
        this.loadingService.hide();
        const blob = result.body;
        if (!blob) return;
        const contentDisposition = result.headers.get('content-disposition');
        let fileName = `CDC_PagesStats_${new Date().toISOString().slice(0, 10)}.xlsx`;

        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            fileName = matches[1].replace(/['"]/g, '');
          }
        }

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate('Error on exporting pages stats'));
      }
    })
  }


  getCompletionStatusLabel(name: string): string {
    return CompletionStatus[name as keyof typeof CompletionStatus].toString();
  }

  getFilter(column: string): FilterDescriptor | undefined {
    return this.availableFilters.find(f => f.key === column);
  }

  onTextFilter(column: string, event: KeyboardEvent) {
    var value = (event.target as HTMLInputElement).value.trim().toLowerCase();

    if (!value) {
      delete this.activeFilters[column];
    } else {
      this.activeFilters[column] = {
        type: 'text',
        values: [value]
      };
    }
    this.applyFilter();
  }

  onSelectFilter(column: string, values: string[]) {
    if (!values || values.length === 0) {
      delete this.activeFilters[column];
    } else {
      this.activeFilters[column] = {
        type: 'select',
        values
      };
    }

    this.applyFilter();
  }

  formatFilterValue(column: string, value: string): string {
    if (column === 'pageStatus' || column === 'offerStatus') {
      return this.getCompletionStatusLabel(value);
    }
    return value;
  }

  formatCellValue(column: string, value: any): string {
    return this.columnFormatters[column]
      ? this.columnFormatters[column](value)
      : value;
  }
}
